/**
 * Taux et parametres sociaux — Luxembourg 2026
 * Source: ccss.public.lu/fr/parametres-sociaux.html
 *
 * TOUS LES MONTANTS EN CENTIMES (integer)
 * TOUS LES TAUX EN POURCENTAGE (ex: 2.80 = 2.80%)
 */

// ─── Index et SSM ────────────────────────────────────────────────

export const INDEX_2026 = 968.04

/** SSM mensuel non qualifie (18+) en centimes */
export const SSM_NON_QUALIFIE_CENTS = 270_374

/** SSM mensuel qualifie en centimes */
export const SSM_QUALIFIE_CENTS = 324_448

/** Plafond cotisable mensuel (5x SSM non qualifie) en centimes */
export const PLAFOND_COTISABLE_CENTS = 1_351_868

/** 1/4 du SSM = abattement assiette dependance en centimes */
export const QUART_SSM_CENTS = Math.round(SSM_NON_QUALIFIE_CENTS / 4)

/** SSM horaire non qualifie (base 173.33h) en centimes */
export const SSM_HORAIRE_NON_QUALIFIE_CENTS = 1_560

/** SSM horaire qualifie en centimes */
export const SSM_HORAIRE_QUALIFIE_CENTS = 1_872

// ─── Cotisations salariales ─────────────────────────────────────

export const COTISATIONS_SALARIALES = {
  /** Maladie — prestations en especes */
  maladie_especes: 0.25,
  /** Maladie — soins de sante */
  maladie_soins: 2.80,
  /** Pension */
  pension: 8.50,
  /** Assurance dependance — assiette = brut - 1/4 SSM */
  dependance: 1.40,
} as const

/** Total cotisations salariales HORS dependance (assises sur le brut plafonne) */
export const TOTAL_COTISATIONS_SALARIALES_SUR_BRUT =
  COTISATIONS_SALARIALES.maladie_especes +
  COTISATIONS_SALARIALES.maladie_soins +
  COTISATIONS_SALARIALES.pension
// = 11.55%

// ─── Cotisations patronales ─────────────────────────────────────

export const COTISATIONS_PATRONALES = {
  /** Maladie — prestations en especes */
  maladie_especes: 0.25,
  /** Maladie — soins de sante */
  maladie_soins: 2.80,
  /** Pension */
  pension: 8.50,
  /** Accident du travail (taux par defaut, variable par secteur) */
  accident_travail: 0.65,
  /** Mutualite des employeurs (classe I par defaut) */
  mutualite: 0.69,
  /** Sante au travail */
  sante_travail: 0.14,
} as const

// ─── Classes de mutualite employeur ─────────────────────────────

export const CLASSES_MUTUALITE: Record<string, number> = {
  'I': 0.69,
  'II': 1.39,
  'III': 2.09,
  'IV': 2.66,
}

// ─── Abattements fiscaux mensuels ───────────────────────────────

/** FFO — Forfait frais d'obtention (540 EUR/an = 45 EUR/mois) en centimes */
export const FFO_MENSUEL_CENTS = 4_500

/** FDS — Forfait depenses speciales (480 EUR/an = 40 EUR/mois) en centimes */
export const FDS_MENSUEL_CENTS = 4_000

/** AC — Abattement conjoint : 187,50 EUR/mois pour classe 2 uniquement, en centimes */
export const AC_MENSUEL_CENTS = 18_750

// ─── Credits d'impot mensuels (ajoutes APRES le net) ────────────

/** CIS — Credit d'Impot Salarie mensuel en centimes */
export const CIS_MENSUEL_CENTS = 2_201

/** CIC — Credit d'Impot Conjoint mensuel en centimes — classe 2 */
export const CIC_MENSUEL_CENTS = 2_201

/** CIM — Credit d'Impot Monoparental mensuel en centimes — classe 1a */
export const CIM_MENSUEL_CENTS = 7_500

/** CICO2 — Credit d'impot CO2 mensuel en centimes */
export const CICO2_MENSUEL_CENTS = 705

// ─── Heures supplementaires ─────────────────────────────────────

/** Majoration heures supplementaires */
export const MAJORATION_HEURES_SUP = 40 // 40%

/** Heures de travail mensuelles standard */
export const HEURES_MENSUELLES_STANDARD = 173.33

// ─── Cheques-repas ──────────────────────────────────────────────

/** Valeur max exoneree par cheque-repas en centimes */
export const CHEQUE_REPAS_EXONERE_MAX_CENTS = 1_080

// ─── Avantages en nature ────────────────────────────────────────

/** Avantage voiture: 1.5% du prix neuf TTC par mois (> 18000km/an) */
export const AVANTAGE_VOITURE_TAUX_HAUT = 1.5
/** Avantage voiture: 0.5% du prix neuf TTC par mois (< 18000km/an) - voiture electrique */
export const AVANTAGE_VOITURE_TAUX_BAS = 0.5

// ─── Type pour configuration societe ────────────────────────────

export interface CompanyRatesConfig {
  /** Taux accident du travail (variable par secteur) */
  accidentTravailRate: number
  /** Classe de mutualite (I, II, III, IV) */
  mutualiteClass: keyof typeof CLASSES_MUTUALITE
  /** Frais de deplacement mensuels en centimes (FD — forfait ou reel) */
  fraisDeplacementCents: number
}

export const DEFAULT_COMPANY_RATES: CompanyRatesConfig = {
  accidentTravailRate: COTISATIONS_PATRONALES.accident_travail,
  mutualiteClass: 'I',
  fraisDeplacementCents: 0,
}
